import {FormControl, FormLabel, TextField} from '@mui/material'
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import {useEffect, useRef} from 'react'

interface CustomDateBoxProps {
  id: string
  label: string
  filter: Record<string, any>
  setFilter: (val: Record<string, any>) => void
  compact?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  disableClear?: boolean
  format?: string
  error?: boolean
  // 값이 증가할 때마다 테두리를 파랗게 번쩍이는 피드백 애니메이션 실행(빠른 기간 버튼 등)
  flashKey?: number
}

export const CustomDateBox = ({
  id,
  label,
  filter,
  setFilter,
  compact = false,
  minDate,
  maxDate,
  disableClear = false,
  format = 'YYYY-MM-DD',
  error = false,
  flashKey,
}: CustomDateBoxProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // flashKey가 바뀔 때마다 Web Animations API로 파란 테두리 플래시 재생.
  // element.animate()는 호출마다 즉시 재시작되므로 빠르게 연타해도 매번 보인다.
  useEffect(() => {
    if (!flashKey) return
    const inputRoot = containerRef.current?.querySelector(
      '.MuiInputBase-root'
    ) as HTMLElement | null
    if (!inputRoot) return
    inputRoot.animate(
      [
        {boxShadow: '0 0 0 0 rgba(29, 78, 216, 0.0)'},
        {boxShadow: '0 0 0 4px rgba(29, 78, 216, 0.9)', offset: 0.25},
        {boxShadow: '0 0 0 0 rgba(29, 78, 216, 0.0)'},
      ],
      {duration: 900, easing: 'ease-out'}
    )
  }, [flashKey])

  return (
    <FormControl className={'w-full md:w-[16rem]'} id={id} ref={containerRef}>
      <FormLabel sx={{fontSize: compact ? '0.875rem' : '1rem', marginBottom: '0.25rem'}}>
        {label}
      </FormLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          views={['year', 'month', 'day']}
          value={filter[id] ? dayjs(filter[id]) : null}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(newValue: Dayjs | null) => {
            if (disableClear && !newValue) return
            setFilter({
              ...filter,
              [id]: newValue ? newValue.format(format) : '',
            })
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              autoComplete='off'
              size='small'
              InputProps={{
                ...params.InputProps,
                inputProps: {
                  ...params.inputProps,
                  placeholder: '날짜 선택',
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: compact ? '1.125rem' : '1.5rem',
                  height: compact ? '2.75rem' : '3.5rem',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                },
                '& input': {
                  padding: compact ? '0.5rem' : '0.5rem',
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </FormControl>
  )
}
